import { MicrophoneIcon, StopIcon } from "@heroicons/react/24/outline";

import { SPEECH_LANGUAGES, useSpeechInput } from "@/hooks/useSpeechInput";

/**
 * Dictation for any AI input box (MPPI-00309).
 *
 * Extracted because the microphone existed on exactly one screen per app while
 * the same "Ask" box appears in the header of every page. One component rather
 * than the same forty lines pasted into each: this project's pattern 32 is a
 * capability implemented twice, where a fix lands on one copy and the other
 * keeps the bug.
 *
 * Renders NOTHING where the browser has no speech recognition — Firefox, and
 * some embedded webviews. A visible control that silently does nothing is
 * worse than an absent one.
 *
 * `onTranscript` fires when a phrase completes, and the caller is expected to
 * RUN the query rather than only fill the box: having to speak and then reach
 * for a button is the wrong shape for a voice control.
 */
export function AskMicButton({
  onTranscript,
  lang,
  onLangChange,
  listeningLabel,
  className = "",
  compact = false,
}: {
  onTranscript: (text: string) => void;
  lang: string;
  onLangChange: (lang: string) => void;
  /** Optional hook so the caller can show interim text in its own input. */
  listeningLabel?: (state: { listening: boolean; interim: string }) => void;
  className?: string;
  /** Hides the language selector where there is no room for it. */
  compact?: boolean;
}) {
  const speech = useSpeechInput(onTranscript, lang);
  listeningLabel?.({ listening: speech.listening, interim: speech.interim });

  if (!speech.supported) return null;

  return (
    <span className={`flex items-center gap-1 ${className}`}>
      {!compact && (
        <select
          value={lang}
          onChange={(e) => onLangChange(e.target.value)}
          disabled={speech.listening}
          title="Language you will speak in"
          aria-label="Dictation language"
          className="rounded border-0 bg-transparent py-0 pl-1 pr-4 text-xs text-current opacity-70 focus:outline-none"
        >
          {SPEECH_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code} className="text-slate-900">
              {l.label}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        onClick={() => (speech.listening ? speech.stop() : speech.start())}
        title={speech.listening ? "Stop listening" : "Ask by voice"}
        aria-label={speech.listening ? "Stop listening" : "Ask by voice"}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition ${
          speech.listening
            ? "animate-pulse bg-rose-600 text-white"
            : "opacity-70 hover:bg-white/10 hover:opacity-100"
        }`}
      >
        {speech.listening ? (
          <StopIcon className="h-4 w-4" />
        ) : (
          <MicrophoneIcon className="h-4 w-4" />
        )}
      </button>
    </span>
  );
}
